import { getProductBySku } from "./ProductUtils";
import { multiply } from "./NumberUtils";
import BigNumber from "bignumber.js";
import { addVat } from "./VatUtils";
import { Asset } from "../types/Asset";
import { Product } from "../types/Product";

export interface ICart {
  items: ICartItem[];
}

export interface ICartItem {
  amount: number;
  name: string;
  sku: string;
  realSku?: string;
  image: string;
  isSample: boolean;
  index?: number;
}

/**
 * It is recommended to use these utility functions though CartContextProvider, but they can be used alone for test purpose etc.
 */
export function cartAddItem(
  cartData: ICart,
  cartItem: ICartItem,
  freeSamples: number
) {
  if (
    cartItem.isSample &&
    cartData.items.filter(item => item.isSample).length >= freeSamples
  ) {
    throw {
      code: "cart.dynamic_sample_limit_exceeded",
      vars: {
        amount: freeSamples
      }
    };
  }

  if (cartItem.isSample) {
    const exists =
      cartData.items
        .filter(item => item.isSample === true)
        .filter(item => item.sku === cartItem.sku).length > 0;
    if (exists) {
      // dont add duplicate sample
      return cartData;
    }
  } else {
    const indexExisting = cartData.items
      .filter(item => !item.isSample)
      .findIndex(item => item.sku === cartItem.sku);
    if (indexExisting >= 0) {
      // merge amounts
      return cartItemUpdateAmount(
        cartData,
        indexExisting,
        cartData.items[indexExisting].amount + cartItem.amount
      );
    }
  }
  // add new article
  return {
    ...cartData,
    items: [...cartData.items, { ...cartItem }]
  };
}

export function cartItemUpdateAmount(
  cartData: ICart,
  index: number,
  amount: number
) {
  return {
    ...cartData,
    items: [
      ...cartData.items.slice(0, index),
      { ...cartData.items[index], amount },
      ...cartData.items.slice(index + 1)
    ]
  };
}

export function cartItemRemove(cartData: ICart, index: number) {
  return {
    ...cartData,
    items: [
      ...cartData.items.slice(0, index),
      ...cartData.items.slice(index + 1)
    ]
  };
}

export function cartSetItems(cartData: ICart, items: ICartItem[]) {
  return { ...cartData, items: [...items] };
}

export function cartEmpty(cartData: ICart) {
  return { ...cartData, items: [] };
}

export function cartItemCount(cartData: ICart) {
  return cartData.items.length;
}

export function cartHasItems(cartData: ICart) {
  return cartItemCount(cartData) > 0;
}

export function findCartItem(cartData: ICart, sku: string) {
  return cartData.items.find(item => !item.isSample && sku === item.sku);
}

export function cartItem({
  name,
  sku,
  images
}: {
  name: string;
  sku: string;
  images?: Asset[];
}): ICartItem {
  return {
    amount: 1,
    name,
    sku,
    image: images?.[0]?.styles?.medium || "",
    isSample: false
  };
}

export function sampleCartItem({
  name,
  sku,
  sampleSku,
  images
}: {
  name: string;
  sku: string;
  sampleSku: string;
  images?: Asset[];
}): ICartItem {
  return {
    amount: 1,
    name,
    sku: sampleSku,
    realSku: sku,
    image: images?.[0]?.styles?.medium || "",
    isSample: true
  };
}

export function cartBaseTotal(cartData: ICart, products: Product[]) {
  return cartData.items
    .reduce((accumulator, item) => {
      const product = getProductBySku(products, item.realSku || item.sku);
      if (product) {
        const amount = item.isSample ? 1 : item.amount;
        const basePrice = item.isSample
          ? product?.samplePrice?.basePrice
          : product?.price?.basePrice;
        if (amount && basePrice) {
          return accumulator.plus(new BigNumber(multiply(amount, basePrice)));
        }
      }
      return accumulator;
    }, new BigNumber(0))
    .toNumber();
}

export function cartTotal(cartData: ICart, products: Product[]) {
  return addVat(cartBaseTotal(cartData, products));
}

export function sortByIsSample(a: ICartItem, b: ICartItem) {
  if (a.isSample) return 1;
  if (b.isSample) return -1;
  return 0;
}
